import React, { FC, useMemo } from "react";
import { Link } from "react-router-dom";

import { usePagedRows } from "../../../components/pagination/usePagedRows";
import { getLinkedFirm } from "../../../sessionStorage/sessionStorage";
import { useGetPaymentsDataQuery } from "../../../slices/ReportDataSlice";
import { exportDataToExcel } from "../../../utils/ExportData";
import { FilterMenu } from "../../works/board/FilterMenu";
import { askTable } from "../ask/reportAsk.utils";
import {
  averageDaysToPay,
  changeText,
  paymentMonths,
  paymentsByClient,
  paymentsByMethod,
  yearBefore,
} from "../moreReports.utils";
import {
  formatDate,
  formatMoney,
  isoDay,
  percent,
  presetRange,
} from "../reports.utils";
import { useViewExtra } from "../saved/SavedViewContext";
import { BarColumns } from "../ui/MoreCharts";
import {
  ChoiceMenu,
  DateMenu,
  PeopleClientTypeFilters,
  useReportFilters,
} from "../ui/ReportFilters";
import {
  Card,
  Empty,
  Kpi,
  KpiGrid,
  Legend,
  LINK,
  LoadError,
  Loading,
  Note,
  NUM,
  ReportPage,
  TableBox,
  TD,
  TH,
  useFocusRows,
} from "../ui/ReportUi";
import { useAllInvoices } from "../useReportData";

type GroupBy = "month" | "client";
const GROUPS: { value: GroupBy; label: string }[] = [
  { value: "month", label: "Month" },
  { value: "client", label: "Client" },
];
const TABLE = "Payments";

const shortMoney = (n: number) =>
  n >= 1000 ? `$${(n / 1000).toFixed(1)}k` : formatMoney(n);

/** Payments received (2026-11-05): money that came in, by month, client and method. Billing access only. */
export const PaymentsReport: FC = () => {
  const state = useReportFilters("thisYear");
  const { filters } = state;
  const [by, setBy] = useViewExtra<GroupBy>("groupBy", "month", GROUPS, "By");
  const [methods, setMethods] = React.useState<string[]>([]);
  const orgId = getLinkedFirm()?.orgId || "";
  const range = useMemo(() => presetRange(filters.preset), [filters.preset]);
  const before = useMemo(() => yearBefore(range), [range]);
  const payments = useGetPaymentsDataQuery(
    {
      orgId,
      from: range ? isoDay(range.from) : null,
      to: range ? isoDay(range.to) : null,
    },
    { skip: !orgId },
  );
  const lastYear = useGetPaymentsDataQuery(
    {
      orgId,
      from: before ? isoDay(before.from) : null,
      to: before ? isoDay(before.to) : null,
    },
    { skip: !orgId || !before },
  );
  const invoices = useAllInvoices({
    from: range?.from ?? null,
    to: range?.to ?? null,
    customerIds: filters.clientIds,
  });
  const keep = (p: { customerId?: string | null; method: string }) =>
    (!filters.clientIds.length ||
      filters.clientIds.includes(p.customerId || "")) &&
    (!methods.length || methods.includes(p.method));
  const all = useMemo(() => payments.data?.payments ?? [], [payments.data]);
  const rows = useMemo(
    () => all.filter(keep),
    // `keep` only reads the client and method filters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [all, filters.clientIds, methods],
  );
  const previous = (lastYear.data?.payments ?? []).filter(keep);
  const received = rows.reduce((s, p) => s + p.amount, 0);
  const receivedBefore = previous.reduce((s, p) => s + p.amount, 0);
  const billed = invoices.data.reduce((s, i) => s + (i.total || 0), 0);
  const methodRows = useMemo(() => paymentsByMethod(rows), [rows]);
  const months = useMemo(
    () => paymentMonths(rows, invoices.data, range),
    [rows, invoices.data, range],
  );
  const clients = useMemo(() => paymentsByClient(rows), [rows]);
  const avgDays = averageDaysToPay(rows);
  const top = methodRows[0];
  const isLoading = payments.isLoading || invoices.isLoading;
  const v = (x: React.ReactNode) => (isLoading ? "…" : x);
  const change = changeText(received, receivedBefore);
  const methodOptions = useMemo(
    () => paymentsByMethod(all).map((m) => ({ value: m.name, label: m.name })),
    [all],
  );

  const shown = useFocusRows(TABLE, rows, (p) => p.id);
  const { pageRows, pager } = usePagedRows(shown);
  const clientShown = useFocusRows("Received by client", clients, (c) => c.key);
  const clientPage = usePagedRows(clientShown);

  const onExport = () =>
    exportDataToExcel(
      rows.map((p) => ({
        Date: formatDate(p.date),
        Client: p.clientName,
        Method: p.method,
        Amount: Math.round(p.amount * 100) / 100,
        "Days to pay": p.daysToPay ?? "",
      })),
      "Payments received",
    );

  const ask = useMemo(
    () => ({
      title: "Payments received",
      path: "/reports/payments",
      description:
        "Payments received in the chosen dates, compared with what was billed.",
      kpis: [
        { label: "Received", value: formatMoney(received) },
        { label: "Billed", value: formatMoney(billed) },
        {
          label: "Average days to pay",
          value: avgDays === null ? "—" : `${avgDays} days`,
        },
        {
          label: "Most used method",
          value: top ? `${top.name} (${top.pct}%)` : "—",
        },
      ],
      tables: [
        askTable(
          "Billed vs. received by month",
          months,
          [
            {
              key: "month",
              label: "Month",
              kind: "text",
              value: (m) => m.label,
            },
            {
              key: "billed",
              label: "Billed",
              kind: "money",
              value: (m) => m.billed,
            },
            {
              key: "received",
              label: "Received",
              kind: "money",
              value: (m) => m.received,
            },
          ],
          (m) => m.key,
        ),
        askTable(
          "By payment method",
          methodRows,
          [
            {
              key: "method",
              label: "Method",
              kind: "text",
              value: (m) => m.name,
            },
            {
              key: "amount",
              label: "Received",
              kind: "money",
              value: (m) => m.amount,
            },
            {
              key: "count",
              label: "Payments",
              kind: "number",
              value: (m) => m.count,
            },
          ],
          (m) => m.name,
        ),
        askTable(
          "Received by client",
          clients,
          [
            {
              key: "client",
              label: "Client",
              kind: "text",
              value: (c) => c.name,
            },
            {
              key: "amount",
              label: "Received",
              kind: "money",
              value: (c) => c.amount,
            },
            {
              key: "count",
              label: "Payments",
              kind: "number",
              value: (c) => c.count,
            },
          ],
          (c) => c.key,
        ),
      ],
    }),
    [months, methodRows, clients, received, billed, avgDays, top],
  );

  return (
    <ReportPage
      title={"Payments received"}
      description={
        "Money that actually came in, compared with what was billed. Only people with billing access see this report."
      }
      onExport={rows.length ? onExport : undefined}
      ask={isLoading ? undefined : ask}
      askSuggestions={[
        "Which month brought in the most?",
        "Who pays the slowest?",
        "How do clients usually pay?",
      ]}
      filters={
        <>
          <DateMenu
            value={filters.preset}
            onChange={(preset) => state.setFilters({ preset })}
          />
          <ChoiceMenu
            label={"Group by"}
            options={GROUPS}
            value={by}
            onChange={setBy}
          />
          <PeopleClientTypeFilters
            state={state}
            showPeople={false}
            showWorkTypes={false}
          />
          <FilterMenu
            placeholder={"Payment method"}
            options={methodOptions}
            selected={methods}
            onChange={setMethods}
          />
        </>
      }>
      {(payments.isError || invoices.isError) && <LoadError />}
      <KpiGrid>
        <Kpi
          label={"Received"}
          value={v(formatMoney(received))}
          note={
            change
              ? `${change} vs. last year`
              : "Nothing to compare with last year"
          }
          tone={"green"}
        />
        <Kpi
          label={"Billed"}
          value={v(formatMoney(billed))}
          note={
            billed > 0 ? `${percent(received, billed)}% collected` : undefined
          }
        />
        <Kpi
          label={"Average days to pay"}
          value={v(avgDays === null ? "—" : `${avgDays} days`)}
          note={"From invoice date"}
        />
        <Kpi
          label={"Most used method"}
          value={v(top ? top.name : "—")}
          note={top ? `${top.pct}% of the money received` : undefined}
        />
      </KpiGrid>

      <div
        className={
          "grid gap-[18px] lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]"
        }>
        <Card
          title={
            by === "month"
              ? "Billed vs. received, by month"
              : "Received by client"
          }
          right={
            by === "month" ? (
              <Legend
                items={[
                  { label: "Billed", color: "#C9BEFF" },
                  { label: "Received", color: "#6B4EFF" },
                ]}
              />
            ) : undefined
          }>
          {isLoading ? (
            <Loading />
          ) : by === "month" ? (
            <BarColumns
              height={220}
              series={[
                { key: "billed", label: "Billed", color: "#C9BEFF" },
                { key: "received", label: "Received", color: "#6B4EFF" },
              ]}
              buckets={months.map((m) => ({
                key: m.key,
                label: m.label,
                values: [m.billed, m.received],
                top: m.received ? shortMoney(m.received) : "",
              }))}
              format={formatMoney}
            />
          ) : !clients.length ? (
            <Empty text={"No payments for these dates."} />
          ) : (
            <TableBox pager={clientPage.pager}>
              <thead>
                <tr>
                  <th scope={"col"} className={TH}>
                    Client
                  </th>
                  <th scope={"col"} className={`${TH} ${NUM}`}>
                    Payments
                  </th>
                  <th scope={"col"} className={`${TH} ${NUM}`}>
                    Received
                  </th>
                </tr>
              </thead>
              <tbody>
                {clientPage.pageRows.map((c) => (
                  <tr key={c.key}>
                    <td className={`${TD} font-bold`}>{c.name}</td>
                    <td className={`${TD} ${NUM}`}>{c.count}</td>
                    <td className={`${TD} ${NUM} font-extrabold`}>
                      {formatMoney(c.amount)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </TableBox>
          )}
        </Card>
        <Card title={"By payment method"}>
          {isLoading ? (
            <Loading />
          ) : !methodRows.length ? (
            <Empty text={"No payments for these dates."} />
          ) : (
            <div className={"flex flex-col gap-3.5"}>
              {methodRows.map((m) => (
                <div key={m.name}>
                  <div className={"flex justify-between text-[13.5px]"}>
                    <span className={"font-bold"}>{m.name}</span>
                    <span>{formatMoney(m.amount)}</span>
                  </div>
                  <div
                    className={
                      "mt-1.5 h-2 overflow-hidden rounded-full bg-[#EDEFF3]"
                    }>
                    <div
                      className={"h-full rounded-full bg-[#6B4EFF]"}
                      style={{ width: `${m.pct}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      <Card title={TABLE}>
        {isLoading ? (
          <Loading />
        ) : !rows.length ? (
          <Empty text={"No payments for these dates."} />
        ) : (
          <TableBox pager={pager}>
            <thead>
              <tr>
                <th scope={"col"} className={TH}>
                  Date
                </th>
                <th scope={"col"} className={TH}>
                  Client
                </th>
                <th scope={"col"} className={TH}>
                  Method
                </th>
                <th scope={"col"} className={`${TH} ${NUM}`}>
                  Days to pay
                </th>
                <th scope={"col"} className={`${TH} ${NUM}`}>
                  Amount
                </th>
              </tr>
            </thead>
            <tbody>
              {pageRows.map((p) => (
                <tr key={p.id}>
                  <td className={TD}>
                    <Link to={`/billing/payments/${p.id}`} className={LINK}>
                      {formatDate(p.date)}
                    </Link>
                  </td>
                  <td className={`${TD} font-bold`}>{p.clientName}</td>
                  <td className={TD}>{p.method}</td>
                  <td className={`${TD} ${NUM}`}>{p.daysToPay ?? "—"}</td>
                  <td className={`${TD} ${NUM} font-extrabold`}>
                    {formatMoney(p.amount)}
                  </td>
                </tr>
              ))}
            </tbody>
          </TableBox>
        )}
      </Card>

      <Note>
        Received = payments dated in the chosen dates; billed = invoices dated
        in the same dates. Days to pay = from the invoice date to the payment
        date (weighted by the amount when one payment covers several invoices).
      </Note>
    </ReportPage>
  );
};
